import React, { useRef, useState, useEffect } from 'react';
import { Box, SystemStyleObject, Text, Tooltip } from '@chakra-ui/react';
import {
  chakraComponents,
  Select as ReactSelect,
  SingleValueProps,
  PlaceholderProps,
} from 'chakra-react-select';
import { CustomOption } from '../common/components/TenantsByProductOptions/SelectedProductRow';
import { AuditLogsSupportedSystemName } from './constants';

// Override react-select disabled opacity to maintain WCAG contrast
// Will have to update chakra UI or the theming entirely then remove this.
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    /* Target placeholder wrapper by react-select structure */
    [class*="placeholder"] {
      opacity: 1 !important;
    }
  `;
  document.head.appendChild(style);
}

const CustomPlaceholder = (props: PlaceholderProps) => {
  return (
    <chakraComponents.Placeholder {...props}>
      <Box as='span' color='gray'>
        {props.children}
      </Box>
    </chakraComponents.Placeholder>
  );
};

const CustomSingleValue = (
  props: SingleValueProps & {
    onOverflowChange?: (isOverflowing: boolean) => void;
  },
) => {
  const { children, onOverflowChange, ...rest } = props;
  const text = children as string;
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const isTextOverflowing =
          textRef.current.scrollWidth > textRef.current.clientWidth;
        onOverflowChange?.(isTextOverflowing);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [text, onOverflowChange]);

  return (
    <chakraComponents.SingleValue {...rest}>
      <Text
        ref={textRef}
        color='inherit'
        fontSize='md'
        whiteSpace='nowrap'
        textAlign='left'
        w='full'
        overflow='hidden'
        textOverflow='ellipsis'
      >
        {text}
      </Text>
    </chakraComponents.SingleValue>
  );
};

interface SystemSelectorProps {
  systemsList: { value: string; label: string }[];
  onSystemChange: (system: AuditLogsSupportedSystemName) => void;
  isDisabled?: boolean;
  selectedValue?: string;
  isError?: boolean;
}

export const SystemSelector = ({
  systemsList,
  onSystemChange,
  isDisabled = false,
  selectedValue,
  isError = false,
}: SystemSelectorProps) => {
  const [isOverflowing, setIsOverflowing] = useState(false);

  const selectedOption = systemsList.find(
    (option) => option.value === selectedValue,
  );
  const selectedLabel = selectedOption?.label || '';

  const handleOverflowChange = (overflowing: boolean) => {
    setIsOverflowing(overflowing);
  };

  const getPlaceholder = () => {
    if (isError) return 'Failed to load products';
    if (isDisabled) return 'Loading products...';

    return 'Select Product';
  };

  return (
    <Box data-testid='system-select-wrapper' w='100%'>
      <Tooltip
        label={selectedLabel}
        aria-label={selectedLabel}
        isDisabled={!isOverflowing || !selectedLabel}
        openDelay={300}
        hasArrow
        placement='top'
      >
        <Box w='100%'>
          <ReactSelect
            aria-label='Select product'
            placeholder={getPlaceholder()}
            maxMenuHeight={274}
            components={{
              ...chakraComponents,
              Option: CustomOption,
              Placeholder: CustomPlaceholder,
              SingleValue: (props: any) => (
                <CustomSingleValue
                  {...props}
                  onOverflowChange={handleOverflowChange}
                />
              ),
            }}
            selectedOptionColorScheme='primary'
            isDisabled={isDisabled}
            value={selectedOption || null}
            chakraStyles={{
              menuList: (provided: SystemStyleObject) => ({
                ...provided,
                p: 0,
              }),
            }}
            options={systemsList}
            onChange={(data) =>
              onSystemChange(
                (data as { value: AuditLogsSupportedSystemName })?.value || '',
              )
            }
          />
        </Box>
      </Tooltip>
    </Box>
  );
};

interface TenantSelectorProps {
  tenantsList: { value: string; label: string }[];
  onTenantChange: (tenant: string) => void;
  isDisabled?: boolean;
  selectedValue?: string;
  selectedSystemId?: string;
  isLoading?: boolean;
  isError?: boolean;
}

export const TenantSelector = ({
  tenantsList,
  onTenantChange,
  isDisabled = false,
  selectedValue,
  selectedSystemId,
  isLoading,
  isError = false,
}: TenantSelectorProps) => {
  const [isOverflowing, setIsOverflowing] = useState(false);

  const selectedOption = tenantsList.find(
    (option) => option.value === selectedValue,
  );
  const selectedLabel = selectedOption?.label || '';

  const handleOverflowChange = (overflowing: boolean) => {
    setIsOverflowing(overflowing);
  };

  const getPlaceholder = () => {
    if (isError) return 'Failed to load tenants';
    if (isLoading) return 'Loading tenants...';
    if (!selectedSystemId) return 'Select a product first';
    if (selectedSystemId && tenantsList.length === 0) return 'Not required';

    return 'Select Tenant';
  };

  return (
    <Box data-testid='tenant-select-wrapper' w='100%'>
      <Tooltip
        label={selectedLabel}
        aria-label={selectedLabel}
        isDisabled={!isOverflowing || !selectedLabel}
        openDelay={300}
        hasArrow
        placement='top'
      >
        <Box w='100%'>
          <ReactSelect
            aria-label='Select tenant'
            placeholder={getPlaceholder()}
            maxMenuHeight={274}
            components={{
              ...chakraComponents,
              Option: CustomOption,
              Placeholder: CustomPlaceholder,
              SingleValue: (props: any) => (
                <CustomSingleValue
                  {...props}
                  onOverflowChange={handleOverflowChange}
                />
              ),
            }}
            selectedOptionColorScheme='primary'
            isDisabled={isDisabled}
            value={selectedOption || null}
            chakraStyles={{
              menuList: (provided: SystemStyleObject) => ({
                ...provided,
                p: 0,
              }),
            }}
            options={tenantsList}
            onChange={(data) =>
              onTenantChange((data as { value: string })?.value || '')
            }
          />
        </Box>
      </Tooltip>
    </Box>
  );
};
